import { Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, keepPreviousData } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SentryRouteTracker } from "@/components/SentryRouteTracker";
import Login from "./pages/Login";
import {
  Team,
  EditorProfile,
  Profile,
  
  Titles,
  
  Reviews,
  Ideas,
  Inbox,
  Messages,
  GetPaid,
  Emails,
  Sops,
  Debug,
  NotFound,
  Invite,
  Unsubscribe,
} from "@/lib/routeChunks";

// Bumped when query shapes change; invalidates persisted cache on new deploys.
const BUILD_HASH = (import.meta.env.VITE_BUILD_HASH as string | undefined) ?? "v2";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 1000 * 60 * 60 * 24, // 24h — long enough for persisted entries to rehydrate
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
      placeholderData: keepPreviousData,
    },
  },
});

const QUERY_CACHE_KEY = "tcc-query-cache";
const MAX_PERSISTED_CACHE_BYTES = 2 * 1024 * 1024; // 2MB

// One-time housekeeping: drop oversized or unreadable persisted caches before
// the persister tries to hydrate them. A bloated cache is the most common
// cause of the "stuck on loading" perception on first paint.
function safeStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(QUERY_CACHE_KEY);
    if (raw && raw.length > MAX_PERSISTED_CACHE_BYTES) {
      console.warn(
        `[query-cache] Persisted cache is ${raw.length} bytes (>${MAX_PERSISTED_CACHE_BYTES}); resetting.`,
      );
      window.localStorage.removeItem(QUERY_CACHE_KEY);
    }
    return window.localStorage;
  } catch (e) {
    console.warn("[query-cache] localStorage unavailable, skipping persistence:", e);
    return undefined;
  }
}

const persister = createSyncStoragePersister({
  storage: safeStorage(),
  key: QUERY_CACHE_KEY,
  throttleTime: 1000,
});

function MinimalLoadingFallback() {
  // Layout-shaped placeholder so chunk loads don't flash a full-page skeleton.
  return (
    <div className="flex min-h-screen w-full">
      <div className="hidden md:block w-60 shrink-0 bg-[#171717]" />
      <div className="flex-1" />
    </div>
  );
}

function StuckLoadingPanel() {
  const handleReload = () => window.location.reload();
  const handleResetCache = () => {
    try {
      window.localStorage.removeItem(QUERY_CACHE_KEY);
      window.sessionStorage.clear();
    } catch (e) {
      console.warn("[stuck-loading] cache reset failed:", e);
    }
    window.location.reload();
  };
  return (
    <div className="mt-8 rounded-lg border border-border bg-card p-4 text-sm">
      <p className="font-medium text-foreground">Taking longer than usual?</p>
      <p className="mt-1 text-muted-foreground">
        Your connection or a stale local cache may be holding things up.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={handleReload}
          className="rounded-md border border-border bg-background px-3 py-1.5 text-foreground hover:bg-muted"
        >
          Reload
        </button>
        <button
          onClick={handleResetCache}
          className="rounded-md bg-primary px-3 py-1.5 text-primary-foreground hover:opacity-90"
        >
          Reset app cache &amp; reload
        </button>
      </div>
    </div>
  );
}

function FullLoadingFallback() {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setStuck(true), 6000);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div className="flex min-h-screen w-full">
      <div className="hidden md:block w-60 shrink-0 bg-[#171717]" />
      <div className="flex-1 p-6 lg:p-8">
        <div className="mx-auto max-w-[1400px] space-y-4">
          <div className="h-8 w-48 animate-pulse rounded bg-muted" />
          <div className="h-4 w-72 animate-pulse rounded bg-muted" />
          <div className="mt-6 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-muted" />
            ))}
          </div>
          {stuck && <StuckLoadingPanel />}
        </div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isOwner } = useAuth();
  if (loading) return <FullLoadingFallback />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isOwner) return <Navigate to="/videos" replace />;
  return <>{children}</>;
}

// Back-compat shim: redirects /ideas → /videos while preserving query string
// (e.g. ?ideaId=…). Kept for old emails / bookmarks; can be removed once
// in-flight email links have aged out.
function IdeasToVideosRedirect() {
  const location = useLocation();
  return <Navigate to={`/videos${location.search}${location.hash}`} replace />;
}

const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 1000 * 60 * 60 * 24, // 24h
      buster: BUILD_HASH,
    }}
  >
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <SentryRouteTracker />
            <Suspense fallback={<MinimalLoadingFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/invite/:token" element={<Invite />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/" element={<Navigate to="/videos" replace />} />

                <Route path="/scripts" element={<Navigate to="/videos" replace />} />
                <Route path="/featured" element={<Navigate to="/videos" replace />} />
                <Route path="/linkedin" element={<Navigate to="/videos" replace />} />
                <Route path="/archive/linkedin" element={<Navigate to="/videos" replace />} />
                
                <Route path="/titles" element={<OwnerRoute><Titles /></OwnerRoute>} />
                
                <Route path="/reviews" element={<OwnerRoute><Reviews /></OwnerRoute>} />
                <Route path="/videos" element={<ProtectedRoute><Ideas /></ProtectedRoute>} />
                <Route path="/sops" element={<ProtectedRoute><Sops /></ProtectedRoute>} />
                <Route path="/sops/:courseSlug" element={<ProtectedRoute><Sops /></ProtectedRoute>} />
                <Route path="/ideas" element={<IdeasToVideosRedirect />} />
                <Route path="/team" element={<OwnerRoute><Team /></OwnerRoute>} />
                <Route path="/team/:memberId" element={<OwnerRoute><EditorProfile /></OwnerRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/get-paid" element={<ProtectedRoute><GetPaid /></ProtectedRoute>} />
                <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
                <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
                <Route path="/comments" element={<Navigate to="/inbox" replace />} />
                <Route path="/youtube-comments" element={<Navigate to="/inbox" replace />} />
                <Route path="/emails" element={<OwnerRoute><Emails /></OwnerRoute>} />
                <Route path="/debug" element={<ProtectedRoute><Debug /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </PersistQueryClientProvider>
);

export default App;
